import * as React from "react";
import { createContext, useContext, useState, useEffect, useRef, useMemo } from "react";
import { ImpersonationServices } from "@/services/bedrock/ImpersonationServices";
import { ImpersonatableUsers } from "@/services/bedrock/dtos";
import { useDebounced } from "@/utils/useDebounced";
import { useInfiniteQuery } from "@tanstack/react-query";
import { toast } from "sonner";
import { useImpersonation, useSocialCoachSession } from "@/utils";
import { useMfaChallenge } from "@/providers/MfaChallengeProvider";

interface ImpersonationSwitchDialogContextValue {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  search: string;
  setSearch: React.Dispatch<React.SetStateAction<string>>;
  debouncedSearch: string;
  transitioningUser: ImpersonatableUsers | null;
  setTransitioningUser: React.Dispatch<React.SetStateAction<ImpersonatableUsers | null>>;
  isReverting: boolean;
  setIsReverting: React.Dispatch<React.SetStateAction<boolean>>;
  focusedIndex: number;
  setFocusedIndex: React.Dispatch<React.SetStateAction<number>>;
  scrollContainerRef: React.RefObject<HTMLDivElement>;
  users: ImpersonatableUsers[];
  activeList: ImpersonatableUsers[];
  recentUsers: ImpersonatableUsers[];
  isLoading: boolean;
  isFetching: boolean;
  hasNextPage: boolean;
  isFetchingNextPage: boolean;
  fetchNextPage: () => void;
  handleImpersonate: (user: ImpersonatableUsers) => Promise<void>;
  handleRevertSession: () => Promise<void>;
  handleKeyDown: (e: React.KeyboardEvent<HTMLInputElement>) => void;
  handleScroll: (e: React.UIEvent<HTMLDivElement>) => void;
  getInitials: (name: string | null | undefined) => string;
  session: ReturnType<typeof useSocialCoachSession>["session"];
}

const ImpersonationSwitchDialogContext = createContext<ImpersonationSwitchDialogContextValue | undefined>(undefined);

export const useImpersonationDialogContext = () => {
  const context = useContext(ImpersonationSwitchDialogContext);
  if (!context) {
    throw new Error("useImpersonationDialogContext must be used within an ImpersonationSwitchDialogProvider");
  }
  return context;
};

interface ProviderProps {
  children: React.ReactNode;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

export const ImpersonationSwitchDialogProvider: React.FC<ProviderProps> = ({ children, open, onOpenChange }) => {
  const { session } = useSocialCoachSession();
  const { impersonate, revert, recentUsers } = useImpersonation();
  const { triggerMfa } = useMfaChallenge();
  const [search, setSearch] = useState("");
  const debouncedSearch = useDebounced(search, 300);

  // State to track the currently selected user during context switching
  const [transitioningUser, setTransitioningUser] = useState<ImpersonatableUsers | null>(null);
  const [isReverting, setIsReverting] = useState(false);

  // Highlight/focus index for keyboard navigation
  const [focusedIndex, setFocusedIndex] = useState(0);
  const scrollContainerRef = useRef<HTMLDivElement>(null);

  // Use TanStack useInfiniteQuery for paginated lazy-loading of users
  const {
    data: usersData,
    isLoading,
    isFetching,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
  } = useInfiniteQuery({
    queryKey: ["impersonatableUsers", debouncedSearch],
    queryFn: ({ pageParam = 0 }) =>
      ImpersonationServices.getImpersonatableUsers({
        search: debouncedSearch,
        page: pageParam,
        perPage: 10,
      }),
    initialPageParam: 0,
    getNextPageParam: lastPage => {
      const nextPage = (lastPage.pagination?.page ?? 0) + 1;
      return nextPage < (lastPage.pagination?.totalPages ?? 0) ? nextPage : undefined;
    },
    enabled: open && !transitioningUser, // Stop fetching if transitioning
  });

  const users = useMemo(() => {
    return usersData?.pages.flatMap(page => page.content) || [];
  }, [usersData]);

  // Determine the active flat list of users being displayed
  const activeList = useMemo(() => {
    return search ? users : [...recentUsers, ...users];
  }, [search, users, recentUsers]);

  // Reset focus index when the list changes
  useEffect(() => {
    setFocusedIndex(0);
  }, [activeList]);

  // Reset transitioning state when dialog open state changes
  useEffect(() => {
    if (!open) {
      setTransitioningUser(null);
      setIsReverting(false);
    }
  }, [open]);

  // Auto-scroll the list container to keep the keyboard-focused item in view
  useEffect(() => {
    if (scrollContainerRef.current) {
      const activeElement = scrollContainerRef.current.querySelector<HTMLButtonElement>('[data-focused="true"]');
      if (activeElement) {
        activeElement.scrollIntoView({ block: "nearest" });
      }
    }
  }, [focusedIndex]);

  // Infinite scroll trigger when reaching the bottom of the container
  const handleScroll = (e: React.UIEvent<HTMLDivElement>) => {
    const { scrollTop, scrollHeight, clientHeight } = e.currentTarget;
    if (scrollHeight - scrollTop <= clientHeight + 50) {
      if (hasNextPage && !isFetchingNextPage) {
        fetchNextPage();
      }
    }
  };

  const handleImpersonate = async (user: ImpersonatableUsers) => {
    // If the admin's active session is not yet MFA verified, trigger the Step-Up challenge first!
    if (!session?.mfaVerified) {
      try {
        onOpenChange(false); // Close switcher to prevent key listener overlaps
        await triggerMfa();

        // If MFA succeeds, instantly trigger the impersonation helper to redirect directly (no double loading animation)
        await impersonate(user);
        return;
      } catch (e) {
        // Verification was cancelled or failed. Abort the impersonation.
        toast.error("MFA verification required to switch accounts.");
        onOpenChange(true); // Re-open switcher so they resume right where they left off
        return;
      }
    }

    setTransitioningUser(user);

    // Smooth 1.5s minimum animation duration to make the context shift feel tactile and premium
    const delayPromise = new Promise(resolve => setTimeout(resolve, 1500));

    try {
      // 1. Wait for 1.5s animation to play out
      await delayPromise;

      // 2. Trigger the standardized, compliant impersonation helper (saves to history + clears storage + redirects)
      await impersonate(user);
    } catch (e) {
      toast.error("Failed to switch accounts. Please try again.");
      setTransitioningUser(null);
    }
  };

  const handleRevertSession = async () => {
    setIsReverting(true);

    // Smooth 1.5s minimum animation duration to make the context shift feel tactile and premium
    const delayPromise = new Promise(resolve => setTimeout(resolve, 1500));

    try {
      // 1. Wait for 1.5s animation to play out
      await delayPromise;

      // 2. Trigger standardized revert
      await revert();
    } catch (e) {
      toast.error("Failed to return to your account.");
      setIsReverting(false);
    }
  };

  // Keyboard navigation handler
  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    // Option + R or Alt + R triggers Revert
    if (e.altKey && e.nativeEvent.code === "KeyR") {
      e.preventDefault();
      e.stopPropagation();
      handleRevertSession();
      return;
    }

    if (activeList.length === 0 || transitioningUser || isReverting) return;

    if (e.key === "ArrowDown") {
      e.preventDefault();
      setFocusedIndex(prev => (prev + 1) % activeList.length);
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      setFocusedIndex(prev => (prev - 1 + activeList.length) % activeList.length);
    } else if (e.key === "Enter") {
      e.preventDefault();
      e.stopPropagation();
      const selectedUser = activeList[focusedIndex];
      if (selectedUser) {
        handleImpersonate(selectedUser);
      }
    }
  };

  const getInitials = (name: string | null | undefined) => {
    return (name || "")
      .split(" ")
      .map(n => n[0])
      .join("")
      .toUpperCase()
      .slice(0, 2);
  };

  const contextValue = useMemo<ImpersonationSwitchDialogContextValue>(
    () => ({
      open,
      onOpenChange,
      search,
      setSearch,
      debouncedSearch,
      transitioningUser,
      setTransitioningUser,
      isReverting,
      setIsReverting,
      focusedIndex,
      setFocusedIndex,
      scrollContainerRef,
      users,
      activeList,
      recentUsers,
      isLoading,
      isFetching,
      hasNextPage,
      isFetchingNextPage,
      fetchNextPage,
      handleImpersonate,
      handleRevertSession,
      handleKeyDown,
      handleScroll,
      getInitials,
      session,
    }),
    [
      open,
      onOpenChange,
      search,
      debouncedSearch,
      transitioningUser,
      isReverting,
      focusedIndex,
      users,
      activeList,
      recentUsers,
      isLoading,
      isFetching,
      hasNextPage,
      isFetchingNextPage,
      session,
    ]
  );

  return (
    <ImpersonationSwitchDialogContext.Provider value={contextValue}>
      {children}
    </ImpersonationSwitchDialogContext.Provider>
  );
};
